import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ProjectProtocols } from '../components/ProjectProtocols';
import { ProjectLabor } from '../components/ProjectLabor';
import { Layout } from '../components/Layout';
import { ContactHistory } from '../components/ContactHistory';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Save, Trash2, Clock, Plus, User, Send, MessageSquare, Paperclip, FileText, Image, File, Download, Upload, X, FileDown, Wrench, Calendar, CheckCircle2, PlayCircle, Sparkles, Eye, Truck, Lightbulb, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { OfferCommissionCard } from '../components/OfferCommissionCard';
import { HelpTooltip } from '../components/HelpTooltip';
import { PDFViewer, PDFThumbnail } from '../components/PDFViewer';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { DatePickerField, formatDatePL } from '../components/DatePickerField';
import { pl } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge helper function
const getStatusBadge = (status) => {
  const variants = {
    'lead': { label: 'Lead/do pomiaru', color: 'bg-blue-600 text-[var(--crm-text-primary)]' },
    'akceptacja': { label: 'Akceptacja', color: 'bg-yellow-500 text-black' },
    'brak_akceptacji': { label: 'Brak akceptacji', color: 'bg-gray-500 text-[var(--crm-text-primary)]' },
    'oplacone': { label: 'Opłacone', color: 'bg-emerald-600 text-white' },
    'dostawa': { label: 'Dostawa', color: 'bg-purple-600 text-[var(--crm-text-primary)]' },
    'w_realizacji': { label: 'W realizacji', color: 'bg-orange-500 text-white' },
    'zakonczone': { label: 'Zakończono', color: 'bg-green-700 text-[var(--crm-text-primary)]' },
    // Legacy support
    'do_realizacji': { label: 'Lead/do pomiaru', color: 'bg-blue-600 text-[var(--crm-text-primary)]' },
    'w_toku': { label: 'W realizacji', color: 'bg-orange-500 text-white' },
    'zakończony': { label: 'Zakończono', color: 'bg-green-700 text-[var(--crm-text-primary)]' }
  };
  const variant = variants[status] || { label: status, color: 'bg-slate-500 text-[var(--crm-text-primary)]' };
  return <Badge className={variant.color}>{variant.label}</Badge>;
};

// File type icons
const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'image':
      return <Image className="w-5 h-5 text-green-400" />;
    case 'pdf':
      return <FileText className="w-5 h-5 text-red-400" />;
    case 'word':
      return <FileText className="w-5 h-5 text-blue-400" />;
    case 'excel':
      return <FileText className="w-5 h-5 text-emerald-400" />;
    case 'powerpoint':
      return <FileText className="w-5 h-5 text-orange-400" />;
    case 'archive':
      return <File className="w-5 h-5 text-yellow-400" />;
    default:
      return <File className="w-5 h-5 text-[var(--crm-text-secondary)]" />;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [users, setUsers] = useState([]);
  const [owner, setOwner] = useState(null);
  
  // Delivery date modal
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryDatePick, setDeliveryDatePick] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  
  // Demo mode flag for Duch role
  const isDemoMode = user?.role === 'duch';
  const offerOnlyFinance = project?.finance_level === 'offer_commission';
  const fullFinance = user?.role !== 'montaz' && !offerOnlyFinance && project?.finance_level !== 'none';
  
  // Helper function to format currency (or hide for demo)
  const formatCurrency = (value, showHidden = true) => {
    if (isDemoMode && showHidden) {
      return '[Kwota ukryta]';
    }
    if (value === null || value === undefined) {
      return isDemoMode ? '[Kwota ukryta]' : '0,00 zł';
    }
    return value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
  };
  
  // Helper to show demo toast
  const showDemoToast = () => {
    toast.error('Funkcja niedostępna w trybie Demo', {
      description: 'Ta operacja wymaga pełnych uprawnień.'
    });
  };
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const commentInputRef = useRef(null);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [selectedPdfName, setSelectedPdfName] = useState('');

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Błąd pobierania załączników');
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
    fetchUsers();
    fetchComments();
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (window.location.hash === '#protokol' && project) {
      setTimeout(() => document.getElementById('protokol')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [project]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${id}`);
      setProject(response.data);
      setFormData({
        nazwa: response.data.nazwa,
        klient: response.data.klient,
        adres_inwestycji: response.data.adres_inwestycji || '',
        telefon_klienta: response.data.telefon_klienta || '',
        email_klienta: response.data.email_klienta || '',
        total_offer: response.data.total_offer.toString(),
        costs: {
          okna: response.data.costs.okna.toString(),
          drzwi: response.data.costs.drzwi.toString(),
          montaz: response.data.costs.montaz.toString(),
          dodatkowe: (response.data.costs.dodatkowe || []).map((d, i) => ({ ...d, id: d.id || `dk_${i}_${Date.now()}` }))
        },
        status: response.data.status,
        description: response.data.description || '',
        owner_id: response.data.owner_id || '',
        delivery_date: response.data.delivery_date || null,
        measurement_date: response.data.measurement_date || '',
        montage_date: response.data.montage_date || '',
        rejection_reason: response.data.rejection_reason || ''
      });
    } catch (error) {
      toast.error('Błąd pobierania projektu');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Błąd pobierania użytkowników');
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Błąd pobierania komentarzy');
    }
  };

  // Update owner when project and users are loaded
  useEffect(() => {
    if (project?.owner_id && users.length > 0) {
      const foundOwner = users.find(u => u.id === project.owner_id);
      setOwner(foundOwner);
    }
  }, [project, users]);

  const handleUpdate = async () => {
    if (formData.status === 'brak_akceptacji' && !formData.rejection_reason?.trim()) {
      toast.error('Musisz podać przyczynę odrzucenia.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_offer: parseFloat(formData.total_offer),
        costs: {
          okna: parseFloat(formData.costs.okna) || 0,
          drzwi: parseFloat(formData.costs.drzwi) || 0,
          montaz: parseFloat(formData.costs.montaz) || 0,
          dodatkowe: formData.costs.dodatkowe.map(item => ({
            opis: item.opis,
            kwota: parseFloat(item.kwota) || 0
          }))
        }
      };

      await axios.patch(`${API_URL}/projects/${id}`, payload);
      toast.success('Projekt zaktualizowany');
      setEditing(false);
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd aktualizacji');
    } finally {
      setSaving(false);
    }
  };

  const addDodatkowyKoszt = () => {
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: [...formData.costs.dodatkowe, { id: `dk_${Date.now()}`, opis: '', kwota: '' }]
      }
    });
  };

  const removeDodatkowyKoszt = (index) => {
    const newDodatkowe = formData.costs.dodatkowe.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: newDodatkowe
      }
    });
  };

  const updateDodatkowyKoszt = (index, field, value) => {
    const newDodatkowe = [...formData.costs.dodatkowe];
    newDodatkowe[index][field] = value;
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: newDodatkowe
      }
    });
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/projects/${id}`);
      toast.success('Projekt usunięty');
      navigate('/projects');
    } catch (error) {
      toast.error('Błąd usuwania projektu');
    }
  };

  // Comment handling
  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    // Detect @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setShowMentionDropdown(true);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const insertMention = (mentionedUser) => {
    const cursorPos = commentInputRef.current?.selectionStart || newComment.length;
    const textBeforeCursor = newComment.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textBefore = newComment.substring(0, atIndex);
      const textAfter = newComment.substring(cursorPos);
      const newText = `${textBefore}@${mentionedUser.initials} ${textAfter}`;
      setNewComment(newText);
      
      if (!mentionedUsers.find(u => u.id === mentionedUser.id)) {
        setMentionedUsers([...mentionedUsers, mentionedUser]);
      }
    }
    setShowMentionDropdown(false);
    commentInputRef.current?.focus();
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    setSendingComment(true);
    try {
      await axios.post(`${API_URL}/projects/${id}/comments`, {
        content: newComment,
        mentioned_users: mentionedUsers.map(u => u.id)
      });
      
      setNewComment('');
      setMentionedUsers([]);
      fetchComments();
      toast.success('Komentarz dodany');
    } catch (error) {
      toast.error('Błąd dodawania komentarza');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API_URL}/projects/${id}/comments/${commentId}`);
      fetchComments();
      toast.success('Komentarz usunięty');
    } catch (error) {
      toast.error('Błąd usuwania komentarza');
    }
  };

  const filteredUsersForMention = users.filter(u => 
    u.id !== user?.id && 
    (u.initials.toLowerCase().includes(mentionFilter) || 
     u.email.toLowerCase().includes(mentionFilter))
  );

  // Render comment content with highlighted mentions
  const renderCommentContent = (content) => {
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={`mention-${i}-${part}`} className="text-[#FF6B35] font-semibold">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Attachment handlers
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        await axios.post(`${API_URL}/projects/${id}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Dodano: ${file.name}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Błąd uploadu: ${file.name}`);
      }
    }
    
    setUploading(false);
    fetchAttachments();
    fetchProject(); // Refresh history
  };

  const handleFileInputChange = (e) => {
    handleFileUpload(Array.from(e.target.files));
    e.target.value = ''; // Reset input
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    try {
      await axios.delete(`${API_URL}/projects/${id}/attachments/${attachmentId}`);
      fetchAttachments();
      fetchProject(); // Refresh history
      toast.success('Załącznik usunięty');
    } catch (error) {
      toast.error('Błąd usuwania załącznika');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generowanie PDF...');
      const response = await axios.get(`${API_URL}/projects/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Projekt_${project.nazwa.replace(/\s+/g, '_')}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF wygenerowany!');
    } catch (error) {
      toast.error('Błąd generowania PDF');
      console.error(error);
    }
  };

  // Status suggestion logic
  const [dismissedSuggestion, setDismissedSuggestion] = useState(null);

  const getStatusSuggestion = () => {
    if (!project || !formData) return null;
    const status = project.status;

    // (1) Lead + all costs filled → suggest Akceptacja
    if (status === 'lead') {
      const costs = project.costs || {};
      const hasCosts = (costs.okna > 0 || costs.drzwi > 0 || costs.montaz > 0) ||
        (costs.dodatkowe && costs.dodatkowe.length > 0 && costs.dodatkowe.some(d => d.kwota > 0));
      if (hasCosts && project.total_offer > 0) {
        return { target: 'akceptacja', label: 'Akceptacja', reason: 'Wszystkie koszty są uzupełnione' };
      }
    }

    // (2) Opłacone + delivery_date in the past → suggest Dostawa
    if (status === 'oplacone' && project.delivery_date) {
      const dd = new Date(project.delivery_date);
      if (dd < new Date()) {
        return { target: 'dostawa', label: 'Dostawa', reason: 'Termin dostawy minął' };
      }
    }

    return null;
  };

  const suggestion = getStatusSuggestion();

  const applySuggestion = async () => {
    if (!suggestion) return;
    try {
      const payload = {
        ...formData,
        total_offer: parseFloat(formData.total_offer),
        costs: {
          okna: parseFloat(formData.costs.okna) || 0,
          drzwi: parseFloat(formData.costs.drzwi) || 0,
          montaz: parseFloat(formData.costs.montaz) || 0,
          dodatkowe: formData.costs.dodatkowe.map(i => ({ opis: i.opis, kwota: parseFloat(i.kwota) || 0 }))
        },
        status: suggestion.target,
      };
      // If changing to dostawa and no delivery date set, open the delivery modal
      if (suggestion.target === 'dostawa' && !formData.delivery_date) {
        setPendingStatusChange(suggestion.target);
        setDeliveryDatePick(null);
        setDeliveryModalOpen(true);
        return;
      }
      await axios.patch(`${API_URL}/projects/${id}`, payload);
      toast.success(`Status zmieniony na "${suggestion.label}"`);
      setDismissedSuggestion(null);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zmiany statusu');
    }
  };

  if (loading || !project) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--crm-text-primary)]">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        <div className="mb-6">
          <Button
            onClick={() => navigate('/projects')}
            data-testid="back-to-projects-button"
            className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)] mb-4"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót
          </Button>
          
          {/* Mobile: Stack layout */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight truncate">{project.nazwa}</h1>
              <p className="text-[var(--crm-text-secondary)] mt-1 text-sm md:text-base">Klient: {project.klient}</p>
            </div>
            
            {/* Action buttons - wrap on mobile */}
            <div className="flex flex-wrap gap-2">
              {/* PDF Export button - hidden for montaz role */}
              {user?.role !== 'montaz' && (
                <Button
                  onClick={handleExportPDF}
                  data-testid="export-pdf-button"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none"
                  size="sm"
                >
                  <FileDown className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Eksport </span>PDF
                </Button>
              )}
              
              {user?.role !== 'guest' && user?.role !== 'montaz' && (
                <>
                  {editing ? (
                    <>
                      <Button
                        onClick={() => setEditing(false)}
                        data-testid="cancel-edit-button"
                        className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)]"
                        size="sm"
                      >
                        Anuluj
                      </Button>
                      <Button
                        onClick={isDemoMode ? showDemoToast : handleUpdate}
                        disabled={saving || isDemoMode}
                        data-testid="save-project-button"
                        className={`${isDemoMode ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)]'} text-white flex-1 sm:flex-none`}
                        size="sm"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {isDemoMode ? 'Demo' : saving ? 'Zapisuję...' : 'Zapisz'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={isDemoMode ? showDemoToast : () => setEditing(true)}
                        disabled={isDemoMode}
                        data-testid="edit-project-button"
                        className={`${isDemoMode ? 'bg-gray-500 cursor-not-allowed' : 'bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)]'} text-[var(--crm-text-primary)] flex-1 sm:flex-none`}
                        size="sm"
                      >
                        {isDemoMode ? 'Tryb Demo' : 'Edytuj'}
                      </Button>
                      {!isDemoMode && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid="delete-project-button" className="bg-red-600 hover:bg-red-700 text-white" size="sm">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] mx-4">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-[var(--crm-text-primary)]">Potwierdź usunięcie</AlertDialogTitle>
                            <AlertDialogDescription className="text-[var(--crm-text-secondary)]">
                              Czy na pewno chcesz usunąć ten projekt? Ta operacja jest nieodwracalna.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)]">Anuluj</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                              Usuń
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status suggestion banner */}
        {suggestion && dismissedSuggestion !== suggestion.target && !isDemoMode && user?.role !== 'guest' && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            data-testid="status-suggestion-banner">
            <Lightbulb className="w-5 h-5 text-amber-400 shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[var(--crm-text-primary)] text-sm font-medium">
                Sugerowana zmiana: <span className="text-amber-400">{suggestion.label}</span>
              </p>
              <p className="text-[var(--crm-text-secondary)] text-xs mt-0.5">{suggestion.reason}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={() => setDismissedSuggestion(suggestion.target)}
                data-testid="suggestion-dismiss-btn"
                className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)]">
                Odrzuć
              </Button>
              <Button size="sm" onClick={applySuggestion}
                data-testid="suggestion-apply-btn"
                className="bg-amber-500 hover:bg-amber-600 text-[var(--crm-text-primary)]">
                Zmień status
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Szczegóły Projektu</h2>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Nazwa</Label>
                    <Input
                      value={formData.nazwa}
                      onChange={(e) => setFormData({...formData, nazwa: e.target.value})}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Klient</Label>
                    <Input
                      value={formData.klient}
                      onChange={(e) => setFormData({...formData, klient: e.target.value})}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Adres Inwestycji</Label>
                    <Input
                      value={formData.adres_inwestycji}
                      onChange={(e) => setFormData({...formData, adres_inwestycji: e.target.value})}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      placeholder="ul. Przykładowa 123, Warszawa"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Telefon Klienta</Label>
                      <Input
                        value={formData.telefon_klienta}
                        onChange={(e) => setFormData({...formData, telefon_klienta: e.target.value})}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                        placeholder="+48 123 456 789"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Email Klienta</Label>
                      <Input
                        type="email"
                        value={formData.email_klienta}
                        onChange={(e) => setFormData({...formData, email_klienta: e.target.value})}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                        placeholder="klient@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Opis</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Status</Label>
                    <Select value={formData.status} onValueChange={(val) => {
                      if (val === 'dostawa' && formData.status !== 'dostawa') {
                        setPendingStatusChange(val);
                        setDeliveryDatePick(formData.delivery_date ? new Date(formData.delivery_date) : null);
                        setDeliveryModalOpen(true);
                      } else {
                        setFormData({...formData, status: val});
                      }
                    }}>
                      <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectItem value="lead">Lead/do pomiaru</SelectItem>
                        <SelectItem value="akceptacja">Akceptacja</SelectItem>
                        <SelectItem value="brak_akceptacji">Brak akceptacji</SelectItem>
                        <SelectItem value="oplacone">Opłacone</SelectItem>
                        <SelectItem value="dostawa">Dostawa</SelectItem>
                        <SelectItem value="w_realizacji">W realizacji</SelectItem>
                        <SelectItem value="zakonczone">Zakończono</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.status === 'brak_akceptacji' && (
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Przyczyna odrzucenia <span className="text-red-500">*</span></Label>
                      <Textarea
                        value={formData.rejection_reason}
                        onChange={(e) => setFormData({...formData, rejection_reason: e.target.value})}
                        placeholder="Np. za drogo, klient wybrał konkurencję, brak kontaktu..."
                        rows={3}
                        data-testid="reject-reason-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      />
                    </div>
                  )}
                  {/* Delivery date display when editing and status is 'dostawa' */}
                  {formData.status === 'dostawa' && formData.delivery_date && (
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Data dostawy</Label>
                      <div className="flex items-center gap-2 bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded-md px-3 py-2">
                        <Truck className="w-4 h-4 text-purple-400" />
                        <span className="text-[var(--crm-text-primary)] text-sm">{formatDatePL(formData.delivery_date)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto h-6 px-2 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]"
                          onClick={() => {
                            setDeliveryDatePick(formData.delivery_date ? new Date(formData.delivery_date) : null);
                            setPendingStatusChange('dostawa');
                            setDeliveryModalOpen(true);
                          }}
                        >
                          Zmień
                        </Button>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] flex items-center gap-1.5">Planowana data montażu</Label>
                    <DatePickerField
                      value={formData.montage_date}
                      onChange={(val) => setFormData({...formData, montage_date: val})}
                      placeholder="Wybierz datę"
                      data-testid="edit-montage-date"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[var(--crm-text-secondary)] flex items-center gap-1.5">Data pomiaru <HelpTooltip text="Data planowanego pomiaru u klienta" /></Label>
                      <DatePickerField
                        value={formData.measurement_date}
                        onChange={(val) => setFormData({...formData, measurement_date: val})}
                        placeholder="Wybierz datę"
                        data-testid="edit-measurement-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)] flex items-center gap-1.5">Data dostawy <HelpTooltip text="Termin dostawy materiałów na budowę" /></Label>
                      <DatePickerField
                        value={formData.delivery_date}
                        onChange={(val) => setFormData({...formData, delivery_date: val})}
                        placeholder="Wybierz datę"
                        data-testid="edit-delivery-date"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Właściciel/Manager Projektu</Label>
                    <Select value={formData.owner_id} onValueChange={(val) => setFormData({...formData, owner_id: val})}>
                      <SelectTrigger data-testid="owner-select" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectValue placeholder="Wybierz właściciela" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.initials} - {u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--crm-text-secondary)]">Status:</span>
                    {getStatusBadge(project.status)}
                  </div>
                  
                  {/* Rejection reason display */}
                  {project.status === 'brak_akceptacji' && project.rejection_reason && (
                    <div className="flex items-start gap-2" data-testid="project-rejection-reason">
                      <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <span className="text-[var(--crm-text-secondary)] text-sm">Przyczyna odrzucenia:</span>
                      <span className="text-sm font-medium text-red-400">{project.rejection_reason}</span>
                    </div>
                  )}

                  {/* Delivery date display */}
                  {project.delivery_date && (
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-purple-400" />
                      <span className="text-[var(--crm-text-secondary)] text-sm">Data dostawy:</span>
                      {(() => {
                        const today = new Date(); today.setHours(0,0,0,0);
                        const dd = new Date(project.delivery_date); dd.setHours(0,0,0,0);
                        const diff = Math.ceil((dd - today) / 86400000);
                        const isOverdue = diff < 0 && project.status !== 'w_realizacji' && project.status !== 'zakonczone';
                        const isSoon = diff >= 0 && diff <= 2;
                        return (
                          <span className={`text-sm font-medium ${isOverdue ? 'text-red-400' : isSoon ? 'text-orange-400' : 'text-purple-300'}`}>
                            {formatDatePL(dd)}
                            {isOverdue && ' (opóźnienie!)'}
                            {isSoon && diff === 0 && ' (dzisiaj!)'}
                            {isSoon && diff > 0 && ` (za ${diff} dn.)`}
                          </span>
                        );
                      })()}
                    </div>
                  )}

                  {project.montage_date && (
                    <div className="flex items-center gap-2" data-testid="project-montage-date">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <span className="text-[var(--crm-text-secondary)] text-sm">Planowany montaż:</span>
                      <span className="text-sm font-medium text-amber-300">{formatDatePL(project.montage_date)}</span>
                    </div>
                  )}

                  {/* Measurement date display */}
                  {project.measurement_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-400" />
                      <span className="text-[var(--crm-text-secondary)] text-sm">Data pomiaru:</span>
                      <span className="text-sm font-medium text-blue-300">{formatDatePL(project.measurement_date)}</span>
                    </div>
                  )}
                  
                  {/* Owner display */}
                  {owner && (
                    <div className="flex items-center gap-3 py-2">
                      <span className="text-[var(--crm-text-secondary)]">Manager:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden">
                          {owner.avatar_url ? (
                            <img src={owner.avatar_url} alt={owner.initials} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-[var(--crm-text-primary)]" />
                          )}
                        </div>
                        <span className="text-[var(--crm-text-primary)]">{owner.initials}</span>
                        <span className="text-[var(--crm-text-secondary)] text-sm">({owner.email})</span>
                      </div>
                    </div>
                  )}
                  
                  {project.adres_inwestycji && (
                    <div>
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Adres Inwestycji:</p>
                      <div className="flex items-start gap-2">
                        <p className="text-[var(--crm-text-primary)] flex-1">{project.adres_inwestycji}</p>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.adres_inwestycji)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF6B35] hover:text-[#E85D2E] transition-colors inline-flex items-center gap-1 text-sm"
                          data-testid="view-on-maps-link"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                          </svg>
                          Mapa
                        </a>
                      </div>
                    </div>
                  )}
                  {project.telefon_klienta && (
                    <div>
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Telefon Klienta:</p>
                      <a href={`tel:${project.telefon_klienta}`} className="text-[var(--crm-text-primary)] hover:text-[#FF6B35]">
                        {project.telefon_klienta}
                      </a>
                    </div>
                  )}
                  {project.email_klienta && (
                    <div>
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Email Klienta:</p>
                      <a href={`mailto:${project.email_klienta}`} className="text-[var(--crm-text-primary)] hover:text-[#FF6B35]">
                        {project.email_klienta}
                      </a>
                    </div>
                  )}
                  {project.description && (
                    <div>
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Opis:</p>
                      <p className="text-[var(--crm-text-primary)]">{project.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[var(--crm-text-secondary)] text-sm">Utworzony przez: {project.created_by}</p>
                    <p className="text-[var(--crm-text-secondary)] text-sm">Data utworzenia: {new Date(project.created_at).toLocaleString('pl-PL')}</p>
                  </div>
                </div>
              )}
            </Card>

            {offerOnlyFinance && (
              <OfferCommissionCard project={project} editing={editing} formData={formData} setFormData={setFormData} />
            )}
            {/* Financial section - only for users who see costs and margins */}
            {fullFinance && (
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">Koszty i Marża <HelpTooltip text="Sekcja kalkulacji kosztów zakupu i marży projektu" /></h2>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] flex items-center gap-1.5">Całkowita Oferta (zł) <HelpTooltip text="Wartość oferty dla klienta (cena sprzedaży)" /></Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.total_offer}
                      onChange={(e) => setFormData({...formData, total_offer: e.target.value})}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Okna (zł)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.costs.okna}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, okna: e.target.value}})}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Drzwi (zł)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.costs.drzwi}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, drzwi: e.target.value}})}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[var(--crm-text-secondary)]">Montaż obcy FV (zł)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.costs.montaz}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, montaz: e.target.value}})}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>
                  </div>

                  <div className="border-t border-[var(--crm-border)] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-[var(--crm-text-secondary)]">Dodatkowe Koszty</Label>
                      <Button
                        type="button"
                        onClick={addDodatkowyKoszt}
                        className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white text-sm h-8"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Dodaj
                      </Button>
                    </div>

                    {formData.costs.dodatkowe.length === 0 ? (
                      <p className="text-[var(--crm-text-secondary)] text-sm text-center py-4">
                        Brak dodatkowych kosztów
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {formData.costs.dodatkowe.map((item, index) => (
                          <div key={item.id || `cost-${index}`} className="flex gap-2 items-start">
                            <div className="flex-1">
                              <Input
                                placeholder="Opis kosztu"
                                value={item.opis}
                                onChange={(e) => updateDodatkowyKoszt(index, 'opis', e.target.value)}
                                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                              />
                            </div>
                            <div className="w-32">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Kwota"
                                value={item.kwota}
                                onChange={(e) => updateDodatkowyKoszt(index, 'kwota', e.target.value)}
                                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                              />
                            </div>
                            <Button
                              type="button"
                              onClick={() => removeDodatkowyKoszt(index)}
                              className="bg-red-600 hover:bg-red-700 text-white h-10 w-10 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Demo mode banner */}
                  {isDemoMode && (
                    <div className="bg-gray-500/20 border border-gray-500/40 rounded-lg p-3 mb-4">
                      <p className="text-gray-400 text-sm text-center">
                        🔒 Dane finansowe ukryte w trybie Demo
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2">
                    <span className="text-[var(--crm-text-secondary)] flex items-center gap-1">Oferta <HelpTooltip text="Wartość oferty dla klienta (cena sprzedaży)" /></span>
                    <span className={`font-mono font-semibold ${isDemoMode ? 'text-gray-500' : 'text-[var(--crm-text-primary)]'}`}>
                      {formatCurrency(project.total_offer)}
                    </span>
                  </div>
                  <Separator className="bg-[var(--crm-bg-btn-secondary)]" />
                  <div className="flex justify-between items-center py-1">
                    <span className="text-[var(--crm-text-secondary)] text-sm">Okna</span>
                    <span className={`font-mono text-sm ${isDemoMode ? 'text-gray-500' : 'text-[var(--crm-text-secondary)]'}`}>
                      {formatCurrency(project.costs?.okna)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-[var(--crm-text-secondary)] text-sm">Drzwi</span>
                    <span className={`font-mono text-sm ${isDemoMode ? 'text-gray-500' : 'text-[var(--crm-text-secondary)]'}`}>
                      {formatCurrency(project.costs?.drzwi)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-[var(--crm-text-secondary)] text-sm">Montaż obcy FV</span>
                    <span className={`font-mono text-sm ${isDemoMode ? 'text-gray-500' : 'text-[var(--crm-text-secondary)]'}`}>
                      {formatCurrency(project.costs?.montaz)}
                    </span>
                  </div>
                  
                  {!isDemoMode && project.costs?.dodatkowe && project.costs.dodatkowe.length > 0 && (
                    <>
                      <Separator className="bg-[var(--crm-bg-btn-secondary)]" />
                      <div className="pt-2">
                        <p className="text-[var(--crm-text-secondary)] text-sm mb-2 font-semibold">Dodatkowe Koszty:</p>
                        {project.costs.dodatkowe.map((item, index) => (
                          <div key={item.id || `extra-${index}`} className="flex justify-between items-center py-1 pl-4">
                            <span className="text-[var(--crm-text-secondary)] text-sm">{item.opis}</span>
                            <span className="text-[var(--crm-text-secondary)] font-mono text-sm">{item.kwota.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  <Separator className="bg-[var(--crm-bg-btn-secondary)]" />
                  <div className="flex justify-between items-center py-2 bg-[var(--crm-bg-panel)] px-4 rounded-md">
                    <span className={isDemoMode ? 'text-gray-500 font-semibold' : 'text-emerald-400 font-semibold'}>Marża (przed robocizną) <HelpTooltip text="Różnica między ceną sprzedaży a kosztami zakupu materiałów" /></span>
                    <span className={`font-mono font-bold ${isDemoMode ? 'text-gray-500' : 'text-emerald-400'}`}>
                      {formatCurrency(project.margin)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-[var(--crm-bg-panel)] px-4 rounded-md">
                    <span className={isDemoMode ? 'text-gray-500 font-semibold' : 'text-amber-400 font-semibold'}>Marża %</span>
                    <span className={`font-mono font-bold ${isDemoMode ? 'text-gray-500' : 'text-amber-400'}`}>
                      {isDemoMode ? '[%]' : `${project.margin_percent?.toFixed(2) || 0}%`}
                    </span>
                  </div>
                  
                  {/* Labor costs from Usługi */}
                  {!isDemoMode && project.labor_cost > 0 && (
                    <>
                      <Separator className="bg-[var(--crm-bg-btn-secondary)] my-2" />
                      <div className="flex justify-between items-center py-2 bg-red-500/10 px-4 rounded-md border border-red-500/30">
                        <span className="text-red-400 font-semibold flex items-center gap-1.5">Robocizna (Usługi) <HelpTooltip text="Koszty robocizny pobrane z modułu Kadry / Usługi" /></span>
                        <span className="text-red-400 font-mono font-bold">-{project.labor_cost.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-[var(--crm-bg-panel)] px-4 rounded-md border-2 border-emerald-500/50">
                        <span className="text-emerald-400 font-bold flex items-center gap-1.5">MARŻA NETTO <HelpTooltip text="Marża brutto minus koszty robocizny" /></span>
                        <span className={`font-mono font-bold text-lg ${project.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {project.net_margin.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-[var(--crm-bg-panel)] px-4 rounded-md">
                        <span className="text-amber-400 font-semibold flex items-center gap-1.5">Marża netto % <HelpTooltip text="Procentowa marża netto: (marża netto / oferta) × 100" /></span>
                        <span className={`font-mono font-bold ${project.net_margin_percent >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                          {project.net_margin_percent?.toFixed(2) || '0.00'}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>
            )}

            {/* Comments Section */}
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Komentarze ({comments.length})
              </h2>
              
              {/* Comment Input */}
              {user?.role !== 'guest' && (
                <div className="mb-6 relative">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt={user.initials} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-[var(--crm-text-primary)]" />
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <Textarea
                        ref={commentInputRef}
                        placeholder="Napisz komentarz... użyj @inicjały aby oznaczyć użytkownika"
                        value={newComment}
                        onChange={handleCommentChange}
                        data-testid="comment-input"
                        rows={3}
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] resize-none"
                      />
                      
                      {/* Mention Dropdown */}
                      {showMentionDropdown && filteredUsersForMention.length > 0 && (
                        <div className="absolute z-50 mt-1 w-64 bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded-md shadow-lg">
                          {filteredUsersForMention.slice(0, 5).map(u => (
                            <button
                              key={u.id}
                              onClick={() => insertMention(u)}
                              className="w-full px-4 py-2 text-left hover:bg-[var(--crm-bg-dialog)] flex items-center gap-2"
                            >
                              <div className="w-6 h-6 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden">
                                {u.avatar_url ? (
                                  <img src={u.avatar_url} alt={u.initials} className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-3 h-3 text-[var(--crm-text-primary)]" />
                                )}
                              </div>
                              <span className="text-[var(--crm-text-primary)] text-sm">{u.initials}</span>
                              <span className="text-[var(--crm-text-secondary)] text-xs truncate">{u.email}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-[var(--crm-text-muted)] text-xs">
                          Tip: Użyj @ aby oznaczyć użytkownika i wysłać powiadomienie
                        </p>
                        <Button
                          onClick={handleSubmitComment}
                          disabled={sendingComment || !newComment.trim()}
                          data-testid="submit-comment-button"
                          className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {sendingComment ? 'Wysyłanie...' : 'Wyślij'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Comments List */}
              {comments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-[var(--crm-text-muted)] mx-auto mb-3" />
                  <p className="text-[var(--crm-text-secondary)]">Brak komentarzy. Bądź pierwszy!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-4 bg-[var(--crm-bg-panel)] rounded-lg" data-testid={`comment-${comment.id}`}>
                      <div className="w-10 h-10 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden flex-shrink-0">
                        {comment.author_avatar ? (
                          <img src={comment.author_avatar} alt={comment.author_initials} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-[var(--crm-text-primary)]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[var(--crm-text-primary)] font-medium">{comment.author_initials}</span>
                            <span className="text-[var(--crm-text-muted)] text-xs">{comment.author_email}</span>
                            {comment.via && <Badge className="bg-sky-500/15 text-sky-400 text-xs px-1.5 py-0" data-testid="comment-via-api">{comment.via}</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[var(--crm-text-muted)] text-xs">
                              {new Date(comment.created_at).toLocaleString('pl-PL')}
                            </span>
                          </div>
                        </div>
                        <p className="text-[var(--crm-text-secondary)] whitespace-pre-wrap">
                          {renderCommentContent(comment.content)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Attachments Section */}
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Załączniki ({attachments.length})
              </h2>
              
              {/* Upload Area */}
              {user?.role !== 'guest' && (
                <div className="mb-6">
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      dragOver 
                        ? 'border-[#FF6B35] bg-[#FF6B35]/10' 
                        : 'border-[var(--crm-border)] hover:border-[#FF6B35]/50'
                    }`}
                    data-testid="attachment-drop-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileInputChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip,.rar"
                    />
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOver ? 'text-[#FF6B35]' : 'text-[var(--crm-text-muted)]'}`} />
                    <p className="text-[var(--crm-text-secondary)] mb-1">
                      {uploading ? 'Wgrywanie...' : 'Przeciągnij pliki tutaj lub kliknij aby wybrać'}
                    </p>
                    <p className="text-[var(--crm-text-muted)] text-xs">
                      PDF, Word, Excel, obrazy, archiwa (max 20MB)
                    </p>
                  </div>
                </div>
              )}
              
              {/* Attachments List */}
              {attachments.length === 0 ? (
                <div className="text-center py-8">
                  <Paperclip className="w-12 h-12 text-[var(--crm-text-muted)] mx-auto mb-3" />
                  <p className="text-[var(--crm-text-secondary)]">Brak załączników</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {attachments.map((attachment) => {
                    const isPdf = attachment.file_type === 'application/pdf' || attachment.original_filename?.toLowerCase().endsWith('.pdf');
                    const isImage = attachment.file_type?.startsWith('image/');
                    const canPreview = isPdf || isImage;
                    
                    return (
                      <div 
                        key={attachment.id} 
                        className="flex items-center gap-3 p-3 bg-[var(--crm-bg-panel)] rounded-lg group"
                        data-testid={`attachment-${attachment.id}`}
                      >
                        {/* Thumbnail preview */}
                        <div className="relative">
                          {isImage ? (
                            <PDFThumbnail 
                              url={attachment.url} 
                              filename={attachment.original_filename}
                            />
                          ) : isPdf ? (
                            <PDFThumbnail 
                              url={attachment.url} 
                              filename={attachment.original_filename}
                            />
                          ) : (
                            getFileIcon(attachment.file_type)
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-[var(--crm-text-primary)] text-sm truncate">{attachment.original_filename}</p>
                          <p className="text-[var(--crm-text-muted)] text-xs">
                            {formatFileSize(attachment.file_size)} • {attachment.uploaded_by_initials} • {new Date(attachment.created_at).toLocaleDateString('pl-PL')}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Preview button for PDF/images */}
                          {canPreview && (
                            <button
                              onClick={() => {
                                setSelectedPdfUrl(attachment.url);
                                setSelectedPdfName(attachment.original_filename);
                                setPdfViewerOpen(true);
                              }}
                              className="p-2 hover:bg-[#FF6B35]/20 rounded-md transition-colors"
                              title="Podgląd"
                              data-testid={`preview-${attachment.id}`}
                            >
                              <Eye className="w-4 h-4 text-[#FF6B35] hover:text-[#FF8C5A]" />
                            </button>
                          )}
                          
                          <a
                            href={attachment.url}
                            download={attachment.original_filename}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-[var(--crm-bg-btn-secondary)] rounded-md transition-colors"
                            data-testid={`download-${attachment.id}`}
                          >
                            <Download className="w-4 h-4 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]" />
                          </a>
                          {user?.role !== 'guest' && (
                            <button
                              onClick={() => handleDeleteAttachment(attachment.id, attachment.original_filename)}
                              className="p-2 hover:bg-red-600/20 rounded-md transition-colors"
                              data-testid={`delete-attachment-${attachment.id}`}
                            >
                              <X className="w-4 h-4 text-[var(--crm-text-secondary)] hover:text-red-400" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* PDF Viewer Modal */}
            <PDFViewer
              url={selectedPdfUrl}
              filename={selectedPdfName}
              isOpen={pdfViewerOpen}
              onClose={() => {
                setPdfViewerOpen(false);
                setSelectedPdfUrl(null);
                setSelectedPdfName('');
              }}
              isMobile={window.innerWidth < 768}
            />

            <ProjectProtocols projectId={id} />

            {fullFinance && !isDemoMode && <ProjectLabor projectId={id} />}

            {/* AI Analysis Section - hidden for montaz role */}
            {user?.role !== 'montaz' && (project.status === 'zakonczone' || project.status === 'zakończony') && (
              <Card className="bg-gradient-to-br from-purple-900/30 to-[#243b5e] border-purple-500/30 p-6">
                <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Analiza AI
                  <HelpTooltip text="Automatyczna analiza projektu generowana przez AI po zakończeniu zlecenia" />
                </h2>
                {project.analiza_ai ? (
                  <div className="space-y-4">
                    <div className="bg-[var(--crm-bg-panel)]/50 rounded-lg p-4 border border-purple-500/20">
                      <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{project.analiza_ai}</p>
                    </div>
                    {project.completed_at && (
                      <p className="text-purple-400/70 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Wygenerowano: {new Date(project.completed_at).toLocaleString('pl-PL')}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-[var(--crm-bg-panel)]/50 rounded-lg p-4 border border-purple-500/20">
                    <p className="text-[var(--crm-text-secondary)] text-sm">
                      Analiza AI zostanie wygenerowana automatycznie przy zakończeniu zlecenia.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Contact History */}
            <ContactHistory entityType="project" entityId={id} />

            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6" data-testid="project-history-card">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF6B35]" />
                Historia Zmian
              </h2>
              {project.history && project.history.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-[var(--crm-bg-btn-secondary)]" />
                  <div className="space-y-4">
                    {project.history.slice().reverse().map((entry, idx) => {
                      const isStatusChange = entry.changes && entry.changes.status;
                      const isCreation = entry.action === 'Projekt utworzony';
                      return (
                        <div key={`history-${idx}-${entry.timestamp}`} className="relative pl-10" data-testid={`history-entry-${idx}`}>
                          {/* Timeline dot */}
                          <div className={`absolute left-[9px] top-1.5 w-3 h-3 rounded-full border-2 ${
                            isStatusChange ? 'bg-[#FF6B35] border-[#FF6B35]' : isCreation ? 'bg-emerald-500 border-emerald-500' : 'bg-[var(--crm-bg-btn-secondary)] border-[#4a6a9e]'
                          }`} />
                          <div className={`rounded-lg p-3 ${isStatusChange ? 'bg-[#FF6B35]/5 border border-[#FF6B35]/20' : 'bg-[var(--crm-bg-panel)] border border-[var(--crm-border)]/50'}`}>
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-[var(--crm-text-secondary)] text-xs">
                                {new Date(entry.timestamp).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <Badge className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] text-xs px-1.5 py-0">{entry.user_initials}</Badge>
                              {entry.user_email && <span className="text-[var(--crm-text-muted)] text-xs hidden sm:inline">{entry.user_email}</span>}
                              {entry.via && <Badge className="bg-sky-500/15 text-sky-400 text-xs px-1.5 py-0" data-testid="history-via-api">{entry.via}</Badge>}
                            </div>
                            {isStatusChange ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-red-400/80 text-sm line-through">{entry.changes.status.old}</span>
                                <span className="text-[var(--crm-text-muted)]">→</span>
                                <span className="text-emerald-400 text-sm font-semibold">{entry.changes.status.new}</span>
                              </div>
                            ) : (
                              <p className="text-[var(--crm-text-primary)] text-sm">{entry.action}</p>
                            )}
                            {entry.changes && Object.entries(entry.changes).filter(([k]) => k !== 'status').length > 0 && (
                              <div className="mt-1.5 text-xs text-[var(--crm-text-secondary)] space-y-0.5">
                                {Object.entries(entry.changes).filter(([k]) => k !== 'status').map(([key, value]) => (
                                  <p key={key}>{key}: <span className="text-[var(--crm-text-muted)]">{value.old}</span> → <span className="text-[var(--crm-text-primary)]">{value.new}</span></p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-[var(--crm-text-secondary)] text-sm">Brak historii zmian</p>
              )}
            </Card>
          </div>

          <div>
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 sticky top-8">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Szybki Podgląd</h2>
              <div className="space-y-4">
                {/* Owner Quick View */}
                {owner && (
                  <div className="bg-[var(--crm-bg-panel)] p-4 rounded-md">
                    <p className="text-[var(--crm-text-secondary)] text-sm mb-2">Manager Projektu</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden">
                        {owner.avatar_url ? (
                          <img src={owner.avatar_url} alt={owner.initials} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-[var(--crm-text-primary)]" />
                        )}
                      </div>
                      <span className="text-[var(--crm-text-primary)] font-medium">{owner.initials}</span>
                    </div>
                  </div>
                )}
                
                {/* Financial info - only for users who see margins */}
                {fullFinance && (
                  <>
                    <div className="bg-[var(--crm-bg-panel)] p-4 rounded-md">
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">
                        {project.labor_cost > 0 ? 'Marża netto' : 'Marża'}
                      </p>
                      <p className={`text-2xl font-manrope font-bold ${(project.net_margin ?? project.margin) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(project.net_margin ?? project.margin).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                      </p>
                      {project.labor_cost > 0 && (
                        <p className="text-red-400/80 text-xs mt-1">
                          (robocizna: -{project.labor_cost.toLocaleString('pl-PL')} zł)
                        </p>
                      )}
                    </div>
                    <div className="bg-[var(--crm-bg-panel)] p-4 rounded-md">
                      <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Rentowność</p>
                      <p className={`text-2xl font-manrope font-bold ${(project.net_margin_percent ?? project.margin_percent) >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                        {(project.net_margin_percent ?? project.margin_percent).toFixed(2)}%
                      </p>
                    </div>
                  </>
                )}
                <div className="bg-[var(--crm-bg-panel)] p-4 rounded-md">
                  <p className="text-[var(--crm-text-secondary)] text-sm mb-1">Status</p>
                  {getStatusBadge(project.status)}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Delivery Date Modal */}
      <Dialog open={deliveryModalOpen} onOpenChange={(open) => {
        if (!open) {
          setDeliveryModalOpen(false);
          setPendingStatusChange(null);
          setDeliveryDatePick(null);
        }
      }}>
        <DialogContent className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-[var(--crm-text-primary)] sm:max-w-md" data-testid="delivery-date-modal-detail">
          <DialogHeader>
            <DialogTitle className="text-[var(--crm-text-primary)] flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-400" />
              Planowana data dostawy
            </DialogTitle>
            <DialogDescription className="text-[var(--crm-text-secondary)]">
              Wybierz planowaną datę dostawy na magazyn/do klienta
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <CalendarComponent
              mode="single"
              selected={deliveryDatePick}
              onSelect={setDeliveryDatePick}
              locale={pl}
              className="rounded-md border border-[var(--crm-border)] bg-[var(--crm-bg-panel)]"
              data-testid="delivery-date-picker-detail"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium text-[var(--crm-text-primary)]",
                nav: "space-x-1 flex items-center",
                nav_button: "h-9 w-9 bg-transparent p-0 opacity-60 hover:opacity-100 border border-[var(--crm-border)] rounded-md inline-flex items-center justify-center text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-[var(--crm-text-secondary)] rounded-md w-10 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FF6B35]/20 [&:has([aria-selected])]:rounded-md",
                day: "h-10 w-10 p-0 font-normal text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)] hover:text-[var(--crm-text-primary)] rounded-md inline-flex items-center justify-center aria-selected:opacity-100 transition-colors",
                day_selected: "bg-[#FF6B35] text-[var(--crm-text-primary)] hover:bg-[#FF6B35] hover:text-[var(--crm-text-primary)] focus:bg-[#FF6B35] focus:text-white",
                day_today: "bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] font-bold",
                day_outside: "text-slate-600 opacity-50",
                day_disabled: "text-slate-600 opacity-30",
                day_hidden: "invisible",
              }}
            />
          </div>
          {deliveryDatePick && (
            <p className="text-center text-sm text-[var(--crm-text-secondary)]">
              Wybrana data: <span className="text-purple-300 font-semibold">{formatDatePL(deliveryDatePick)}</span>
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setDeliveryModalOpen(false); setPendingStatusChange(null); setDeliveryDatePick(null); }}
              className="border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
            >
              Anuluj
            </Button>
            <Button
              onClick={() => {
                if (deliveryDatePick) {
                  const dateStr = deliveryDatePick.toISOString().split('T')[0];
                  setFormData({...formData, status: 'dostawa', delivery_date: dateStr});
                  setDeliveryModalOpen(false);
                  setPendingStatusChange(null);
                  setDeliveryDatePick(null);
                }
              }}
              disabled={!deliveryDatePick}
              data-testid="delivery-date-confirm-detail"
              className="bg-purple-600 hover:bg-purple-700 text-[var(--crm-text-primary)] disabled:opacity-50"
            >
              <Truck className="w-4 h-4 mr-2" />
              Potwierdź dostawę
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
